import { useState, useCallback } from 'react';
import { useToast } from '@/app/providers/ToastProvider';
import { transactionService } from '../services/transactionService';

const todayStr = () => new Date().toISOString().split("T")[0];

export function useTransactions({ householdId, txns, incomeList, allSubCats, defaultPayment, resolvedUserName }) {
  const toast = useToast();
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [txForm, setTxForm] = useState({
    date: todayStr(), description: "", category: "", amount: "",
    who: resolvedUserName, notes: "", paymentMethod: defaultPayment || "Debit Card", type: "expense",
    tags: [],
  });
  const [txErrors, setTxErrors] = useState({});
  const [deleteTxId, setDeleteTxId] = useState(null);
  const [splitTarget, setSplitTarget] = useState(null);

  const openAddTx = useCallback(() => {
    setEditId(null);
    setTxErrors({});
    setTxForm({
      date: todayStr(), description: "", category: allSubCats[0] || "", amount: "",
      who: resolvedUserName, notes: "", paymentMethod: defaultPayment || "Debit Card", type: "expense",
      tags: [],
    });
    setModal("tx");
  }, [allSubCats, resolvedUserName, defaultPayment]);

  const openEditTx = useCallback((t) => {
    setEditId(t.id);
    setTxErrors({});
    setTxForm({ ...t, type: incomeList.includes(t.category) ? "income" : "expense", tags: t.tags || [] });
    setModal("tx");
  }, [incomeList]);

  const saveTx = useCallback(() => {
    const errors = {};
    if (!txForm.description.trim()) errors.description = "Description is required";
    if (!txForm.category) errors.category = "Pick a category";
    const amt = parseFloat(txForm.amount);
    if (!txForm.amount || isNaN(amt) || amt <= 0) errors.amount = "Enter a valid amount";
    if (!txForm.date) errors.date = "Pick a date";
    if (Object.keys(errors).length > 0) { setTxErrors(errors); return; }

    const tx = { ...txForm, amount: amt, id: editId || transactionService.generateId() };
    if (editId) {
      transactionService.update(householdId, txns, editId, tx);
      toast.success('Transaction updated');
    } else {
      transactionService.add(householdId, txns, tx);
      toast.success('Transaction added');
    }
    setModal(null);
    setEditId(null);
    setTxErrors({});
  }, [txForm, editId, householdId, txns, toast]);

  const deleteTx = useCallback((id) => {
    transactionService.delete(householdId, txns, id);
    setDeleteTxId(null);
    toast.success('Transaction deleted');
  }, [householdId, txns, toast]);

  const openSplitTx = useCallback((transaction) => setSplitTarget(transaction), []);
  const closeSplitTx = useCallback(() => setSplitTarget(null), []);

  const splitTx = useCallback((id, splits) => {
    transactionService.split(householdId, txns, id, splits);
    setSplitTarget(null);
    toast.success('Transaction split');
  }, [householdId, txns, toast]);

  const deleteAllSplits = useCallback((splitGroupId) => {
    transactionService.deleteSplitGroup(householdId, txns, splitGroupId);
    setDeleteTxId(null);
    toast.success('All split transactions deleted');
  }, [householdId, txns, toast]);

  return {
    modal, setModal,
    editId, setEditId,
    txForm, setTxForm,
    txErrors, setTxErrors,
    deleteTxId, setDeleteTxId,
    splitTarget, openSplitTx, closeSplitTx, splitTx, deleteAllSplits,
    openAddTx, openEditTx, saveTx, deleteTx,
  };
}
